import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Upload,
  BarChart3,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Studies', href: '/studies', icon: FolderKanban },
  { name: 'Record/Upload', href: '/record-upload', icon: Upload },
  {
    name: 'Analysis',
    href: '#',
    icon: BarChart3,
    children: [
      { name: 'Longitudinal', href: '/data-analysis/longitudinal' },
      { name: 'Inter-Group', href: '/data-analysis/inter-group' },
      { name: 'Intra-Group', href: '/data-analysis/intra-group' },
    ],
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === '#') return false;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isChildActive = (children?: { href: string }[]) => {
    if (!children) return false;
    return children.some((child) => isActive(child.href));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white border-r border-gray-200',
          'transform transition-transform duration-300 ease-in-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <div className="flex items-center gap-3">
            <a
              href="https://autismailab.com"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 transition-colors"
              title="Back to AutismAI Lab"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Lab</span>
            </a>
            <div className="h-4 w-px bg-gray-300" />
            <span className="text-lg font-semibold text-gray-900">E-MotionSpec</span>
          </div>
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={onClose}
          >
            <span className="sr-only">Close sidebar</span>
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col overflow-y-auto px-4 py-4">
          <ul role="list" className="flex flex-1 flex-col gap-y-1">
            {navigation.map((item) => (
              <li key={item.name}>
                {item.children ? (
                  // Parent with children
                  <div>
                    <div
                      className={cn(
                        'group flex items-center gap-x-3 rounded-md px-3 py-2 text-sm font-semibold',
                        isChildActive(item.children)
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isChildActive(item.children)
                            ? 'text-primary-600'
                            : 'text-gray-400 group-hover:text-gray-600'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </div>
                    <ul className="mt-1 space-y-1 pl-9">
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <NavLink
                            to={child.href}
                            onClick={onClose}
                            className={({ isActive: active }) =>
                              cn(
                                'block rounded-md px-3 py-2 text-sm',
                                active
                                  ? 'bg-primary-50 text-primary-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              )
                            }
                          >
                            {child.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  // Single item
                  <NavLink
                    to={item.href}
                    onClick={onClose}
                    className={({ isActive: active }) =>
                      cn(
                        'group flex items-center gap-x-3 rounded-md px-3 py-2 text-sm font-semibold',
                        active
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      )
                    }
                  >
                    <item.icon
                      className={cn(
                        'h-5 w-5 shrink-0',
                        isActive(item.href)
                          ? 'text-primary-600'
                          : 'text-gray-400 group-hover:text-gray-600'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
}
